import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SimplePopover, SimplePopoverPlainOption, DateDropdown } from '@dev/ee-components';
import styled from 'styled-components';
import { DevicesParams, DevicesCampaignParams } from '@/services/ElasticApi';
import dayjs from '@/services/tools/dayjs';
import { useDateFilterData } from '@/services/tools/date';
import { MAX_CAMPAIGNS_LIST_LENGTH, MAX_CHANNELS_LIST_LENGTH } from '@/services/constants';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { ApplicationState as State } from '@/store';
import { debounce } from '@/services/tools/debounce';
import useMobile from '@/services/hooks/useMobile';
import { campaignsOptions } from '../../config/getCampaignOptions';
import { channelsOptions } from '../../config/getChannelOptions';
import { FiltersInterface } from '../../DeviceDetailsInterface';
import MobileFiltersPopover from '../MobileFiltersPopover';

const FiltersStyled = styled.div`
    display: flex;
    gap: 16px;
`;

const FilterPopoverStyled = styled.div`
    max-width: 300px;
`;

type StyledProps = {
    isSmallScreen?: boolean;
};

const DateDropdownWrapper = styled.div<StyledProps>`
    #datedropdown-popover {
        & > div {
            max-height: ${({ isSmallScreen }): number => (isSmallScreen ? 273 : 600)}px;
        }
    }
`;

const calcFromDate = (timeRange: number): Date => dayjs().subtract(timeRange, 'day').toDate();

type RequestParams = DevicesParams | DevicesCampaignParams;

interface DateDropdownFilterInterface {
    params: RequestParams;
    onCalendarChange: (startDate?: Date, endDate?: Date) => void;
}

const DateDropdownFilter: FC<DateDropdownFilterInterface> = (props: DateDropdownFilterInterface): JSX.Element => {
    const { t } = useTranslation();
    const { params, onCalendarChange } = props;
    const startDate = params?.from;
    const endDate = startDate && params?.to;

    const onItemClickDateRange = (
        ev: React.MouseEvent<HTMLElement>,
        { value }: Record<string, string | number>
    ): void => {
        const dateFrom = calcFromDate(value as number);
        const endDate = dayjs().toDate();
        const startDate = value === DateDropdownOptions.All ? null : dateFrom;

        onCalendarChange(startDate, endDate);
    };

    const isSmallScreen = useSelector((state: State) => state?.browser?.lessThan.lg);

    return (
        <DateDropdownWrapper isSmallScreen={isSmallScreen}>
            <DateDropdown
                options={useDateFilterData()}
                defaultPlaceholder={t('last3months')}
                startDate={startDate}
                endDate={endDate}
                value={useDateDropdownValue(false)}
                type="input"
                returnRawData
                calendarOnChange={onCalendarChange}
                onItemClick={onItemClickDateRange}
                buttonVariant="tertiary"
            />
        </DateDropdownWrapper>
    );
};

const Filters: FC<FiltersInterface> = (props: FiltersInterface): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const {
        from,
        to,
        selectedCampaignName,
        selectedChannelName,
        setSelectedCampaignName,
        setSelectedChannelName,
        queryCampaignName,
        setQueryCampaignName,
        queryChannelName,
        setQueryChannelName,
        setFrom,
        setTo,
    } = props;

    const requestParams = useMemo(
        (): RequestParams => ({
            from,
            to,
            campaignName: selectedCampaignName,
            channelName: selectedChannelName,
        }),
        [from, to, selectedCampaignName, selectedChannelName]
    );

    const campaigns = useSelector((state: State) => state?.campaigns);
    const channels = useSelector((state: State) => state?.channels);
    const searchOption: SimplePopoverPlainOption = { label: 'search', value: 'search' };
    const allCampaignsOption: SimplePopoverPlainOption = { label: t('allcampaigns'), value: '' };
    const allChannelsOption: SimplePopoverPlainOption = { label: t('allchannels'), value: '' };
    const [showChannelsFilter, setShowChannelsFilter] = useState(true);

    useEffect(() => {
        !channels.loading && !queryChannelName && setShowChannelsFilter(!!channels.totalCount);
    }, [channels]);

    const handleSearchCampaign = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const query = event?.target?.value ?? '';
        debounce((): void => {
            setQueryCampaignName(query);
        });
    };
    const handleSearchChannel = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const query = event?.target?.value ?? '';
        debounce((): void => {
            setQueryChannelName(query);
        });
    };
    const onCalendarChange = (startDate?: Date, endDate?: Date): void => {
        setTo(endDate?.toJSON() ?? '');
        setFrom(startDate?.toJSON() ?? '');
    };

    return isMobile ? (
        <MobileFiltersPopover
            from={from}
            to={to}
            setFrom={setFrom}
            setTo={setTo}
            selectedCampaignName={selectedCampaignName}
            setSelectedCampaignName={setSelectedCampaignName}
            selectedChannelName={selectedChannelName}
            queryCampaignName={queryCampaignName}
            queryChannelName={queryChannelName}
            setQueryCampaignName={setQueryCampaignName}
        />
    ) : (
        <FiltersStyled>
            {showChannelsFilter && (
                <FilterPopoverStyled>
                    <SimplePopover
                        type="noborder"
                        disableSearch={!queryChannelName && channels?.totalCount <= MAX_CHANNELS_LIST_LENGTH}
                        options={[
                            searchOption,
                            allChannelsOption,
                            ...channelsOptions(channels, t('loading'), t('results'), isMobile),
                        ]}
                        visibleScrollbar
                        maxHeight={375}
                        fullWidth
                        searchPhrase={queryChannelName}
                        keepSearchPhrase
                        defaultValue={selectedChannelName}
                        defaultPlaceholder={t('allchannels')}
                        onItemClick={(e, obj): void => {
                            setSelectedChannelName(obj.value as string);
                            setSelectedCampaignName('');
                        }}
                        onOptionSearch={handleSearchChannel}
                        listItemWidth="300px"
                        listItemMaxLines={2}
                    />
                </FilterPopoverStyled>
            )}
            <FilterPopoverStyled>
                <SimplePopover
                    type="noborder"
                    disableSearch={!queryCampaignName && campaigns.statusCount?.total <= MAX_CAMPAIGNS_LIST_LENGTH}
                    options={[
                        searchOption,
                        allCampaignsOption,
                        ...campaignsOptions(campaigns, t('loading'), t('results'), isMobile),
                    ]}
                    visibleScrollbar
                    maxHeight={375}
                    fullWidth
                    searchPhrase={queryCampaignName}
                    keepSearchPhrase
                    defaultValue={selectedCampaignName}
                    defaultPlaceholder={t('allcampaigns')}
                    onItemClick={(e, obj): void => {
                        setSelectedCampaignName(obj.value as string);
                        setSelectedChannelName('');
                    }}
                    onOptionSearch={handleSearchCampaign}
                    listItemWidth="300px"
                    listItemMaxLines={2}
                />
            </FilterPopoverStyled>
            <DateDropdownFilter params={requestParams} onCalendarChange={onCalendarChange} />
        </FiltersStyled>
    );
};

export default Filters;
